import { useEffect, useMemo, useState } from 'react';
import { getAllThumbs, renderThumb } from './thumbs';
import yieldToPaint from './yieldToPaint';

// One yield per thumbnail. renderThumb is synchronous and cannot be interrupted,
// so responsiveness comes from the gaps between renders, not from within them:
// each thumb is a short block (~14k pixels) with a returned-to-the-event-loop
// pause after it, which keeps taps, scrolls and navigation alive while the strip
// fills in.

const seedFromCache = (entries) => {
    const cached = getAllThumbs();
    const seeded = {};
    entries.forEach(entry => {
        if (cached[entry.id]) seeded[entry.id] = cached[entry.id];
    });
    return seeded;
};

// Thumbnails for the gallery index, cache-first and rendered one at a time.
//
// `enabled` exists to keep the CPU on the thing the user is looking at: while the
// full-size specimen is still rendering, the strip waits. A gallery that fought
// its own foreground image for the main thread would make both slow.
const useThumbs = (entries, enabled) => {

    // The entry list is a fresh array on every store read, so identity is useless
    // as a dependency; the set of ids is what actually changed.
    const ids = useMemo(() => entries.map(e => e.id).join('|'), [entries]);

    const [thumbs, setThumbs] = useState(() => seedFromCache(entries));

    useEffect(() => {
        setThumbs(seedFromCache(entries));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ids]);

    useEffect(() => {
        if (!enabled) return undefined;

        let cancelled = false;

        (async () => {
            // Read the cache once for the whole pass rather than per entry, and
            // track what we produce, so deciding "does this one need rendering?"
            // costs nothing.
            const have = getAllThumbs();

            for (const entry of entries) {
                if (cancelled) return;
                if (have[entry.id]) continue;

                await yieldToPaint();
                if (cancelled) return;

                try {
                    const dataUrl = renderThumb(entry);
                    have[entry.id] = dataUrl;
                    if (cancelled) return;
                    setThumbs(prev => ({ ...prev, [entry.id]: dataUrl }));
                } catch {
                    /* one unrenderable entry must not stall the rest of the strip */
                }
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ids, enabled]);

    return thumbs;
};

export default useThumbs;
